/**
 * §rbac (2026-05-08) — typed wrappers for the /api/admin/roles +
 * /api/admin/operators + /api/admin/me endpoints. All routes go
 * through the shared ``api()`` helper so JWT bearer auth, X-Tenant-ID,
 * and 401 → silent-refresh handling come along for free.
 */

import { api } from "./api";

// ── Permission catalogue ──────────────────────────────────────────
//
// Mirrors the matrix on the /roles page + the seed in
// alembic/versions/training/0003_rbac.py. Anywhere the frontend
// references a permission key by name, that name lives here so the
// TypeScript compiler catches typos.

export type Permission =
  | "overview.view"
  | "conversations.view" | "conversations.edit" | "conversations.delete"
  | "live_calls.view"
  | "orders.view" | "orders.create" | "orders.edit" | "orders.delete"
  | "support.view" | "support.create" | "support.edit" | "support.delete"
  | "customers.view" | "customers.create" | "customers.edit" | "customers.delete"
  | "products.view" | "products.create" | "products.edit" | "products.delete"
  | "qa.view" | "qa.create" | "qa.edit" | "qa.delete"
  | "agent.view" | "agent.edit"
  | "training.view" | "training.create" | "training.edit" | "training.delete"
  | "test_chat.view"
  | "test_voice.view"
  | "analytics.view"
  | "reports.view"
  | "settings.view" | "settings.edit"
  | "roles.view" | "roles.create" | "roles.edit" | "roles.delete"
  | "operators.view" | "operators.create" | "operators.edit" | "operators.delete"
  | "audit_log.view"
  // §master-panel — only granted to the Master Admin role inside
  // the reserved 'master' tenant. Listed here so route guards
  // and the Can/usePermissions hooks can reference them with
  // compile-time safety.
  | "master.tenants.view"
  | "master.tenants.create"
  | "master.tenants.edit"
  | "master.owners.create"
  | "master.owners.edit"
  | "master.owners.reset_password";

// ── Types matching the backend serializers ───────────────────────

export interface Role {
  id: number;
  tenant_id: string;
  name: string;
  description: string;
  is_system: boolean;
  is_locked: boolean;
  permissions: Permission[];
  members: number;
  created_at: string;
  updated_at: string;
}

export interface OperatorAccount {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  active: boolean;
  role_id: number | null;
  role_name: string | null;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  last_login_at: string;
}

export interface MeResponse {
  operator: OperatorAccount | null;
  role: Role | null;
  permissions: Permission[];
  tenant_id: string;
  auth_mode: "jwt_bearer" | "operator_session" | "legacy_secret";
  // §v4.tenant-isolation (2026-06-16) — true when a master operator has
  // entered a customer tenant; the permission set is the Owner-equivalent
  // set for that tenant and the console shows an exit banner.
  impersonating?: boolean;
}

// ── /api/admin/me ────────────────────────────────────────────────

export async function getMe(): Promise<MeResponse> {
  return api<MeResponse>("/api/admin/me");
}

// ── /api/admin/roles ─────────────────────────────────────────────

export async function listRoles(): Promise<Role[]> {
  const res = await api<{ roles: Role[] }>("/api/admin/roles");
  return res.roles;
}

export async function createRole(input: {
  name: string;
  description?: string;
  permissions: Permission[];
}): Promise<Role> {
  const res = await api<{ success: boolean; role: Role }>(
    "/api/admin/roles",
    { method: "POST", body: input },
  );
  return res.role;
}

export async function updateRole(
  id: number,
  patch: {
    name?: string;
    description?: string;
    permissions?: Permission[];
  },
): Promise<Role> {
  const res = await api<{ success: boolean; role: Role }>(
    `/api/admin/roles/${id}`,
    { method: "PUT", body: patch },
  );
  return res.role;
}

export async function deleteRole(id: number): Promise<void> {
  await api<{ success: boolean }>(`/api/admin/roles/${id}`, {
    method: "DELETE",
  });
}

// ── /api/admin/operators ─────────────────────────────────────────

export async function listOperators(): Promise<OperatorAccount[]> {
  const res = await api<{ operators: OperatorAccount[] }>(
    "/api/admin/operators",
  );
  return res.operators;
}

export async function createOperator(input: {
  username: string;
  password: string;
  display_name?: string;
  email?: string;
  role_id?: number;
}): Promise<OperatorAccount> {
  const res = await api<{ success: boolean; operator: OperatorAccount }>(
    "/api/admin/operators",
    { method: "POST", body: input },
  );
  return res.operator;
}

export async function updateOperator(
  id: number,
  patch: {
    username?: string;
    display_name?: string;
    email?: string | null;
    role_id?: number;
    active?: boolean;
    password?: string;
  },
): Promise<OperatorAccount> {
  const res = await api<{ success: boolean; operator: OperatorAccount }>(
    `/api/admin/operators/${id}`,
    { method: "PATCH", body: patch },
  );
  return res.operator;
}

export async function deactivateOperator(id: number): Promise<void> {
  await api<{ success: boolean }>(`/api/admin/operators/${id}`, {
    method: "DELETE",
  });
}

// ── §master-panel — /api/master/* ───────────────────────────────

export interface MasterTenant {
  id: string;
  business_name: string;
  agent_name: string;
  disclosure_language: string;
  locale: string;
  active: boolean;
  created_at: number;
  updated_at: number;
  operator_count: number;
  active_operator_count: number;
  owner_count: number;
  has_owner: boolean;
  owner_emails: string[];
  /** §mission-control-ops — subscription plan + monthly price (AED). "" = unset. */
  plan: string;
  mrr: number;
  /** §mc-demo-excluded — synthetic clone; listed, never counted. */
  demo?: boolean;
  /** §billing.platform — real Stripe subscription state. Optional so existing
   * fixtures/constructors stay valid; "" / 0 = no subscription on file yet. */
  subscription_status?: string;
  trial_end?: number;
  subscription_id?: string;
  customer_id?: string;
  /** §pilot-plan — founder-granted pilot lifecycle blob ({} / absent = none).
   * Written only by pilot_program.py via the /pilot/{action} endpoints. */
  pilot?: PilotState;
  /** §pilot-instrumentation — ISO-3166 alpha-2 country from the tenant's own
   * brand facts ("" = not configured). */
  country?: string;
  /** §pilot-instrumentation — the tenant's resolved money currency
   * (payment_settings.resolve_currency). Tenant money only — platform
   * billing (MRR, plan prices) stays AED. */
  currency?: string;
  /** §pilot-instrumentation — health RAG, computed on read, pilot tenants
   * only (absent otherwise). `reasons` names each failing signal. */
  health?: PilotHealth;
  /** §ai-on-off — is this tenant's agent answering customers? Mirrors the
   * ONE agent_published flag (absent key = published, grandfathered). Distinct
   * from `active` (account suspension) and from the pilot lifecycle. */
  agent_published?: boolean;
}

/** §pilot-instrumentation — one pilot tenant's early-warning score. */
export interface PilotHealth {
  status: "red" | "amber" | "green" | string;
  reasons: string[];
  activated: boolean;
  first_value_seconds: number;
}

/** §pilot-plan — one agreed success criterion (free text, agreed at kickoff). */
export interface PilotCriterion {
  metric: string;
  target: string;
  deadline: string;
}

/** §pilot-plan — mirror of tenant_settings.data_json["pilot"]. `status` is the
 * STORED state; a grace window whose grace_until has passed is effectively
 * "ended" (compute client-side with the current clock, same rule as the
 * backend's effective_status). */
export interface PilotState {
  started_at?: number;
  ends_at?: number;
  status?: "active" | "grace" | "suspended" | "converted" | "ended" | string;
  grace_until?: number;
  extended_once?: boolean;
  criteria?: PilotCriterion[];
  history?: { at: number; event: string; [k: string]: unknown }[];
  /** §pilot-instrumentation — epoch of the first REAL-customer successful
   * outcome (0/absent = not activated yet). Written once per pilot run. */
  activated_at?: number;
  /** §pilot-instrumentation — activated_at − started_at (time-to-first-value). */
  first_value_seconds?: number;
  /** §pilot-instrumentation — founder-alert dedup markers {key: epoch}. */
  alerts_sent?: Record<string, number>;
}

export type PilotAction = "start" | "extend" | "end" | "suspend" | "reactivate" | "convert";

/** §pilot-plan — drive a tenant's pilot lifecycle (master-gated). */
export async function masterTenantPilot(
  tenantId: string,
  action: PilotAction,
  body: { days?: number; reason?: string; plan?: string; criteria?: PilotCriterion[] } = {},
): Promise<{ ok: boolean; pilot: PilotState }> {
  return api<{ ok: boolean; pilot: PilotState }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/pilot/${action}`,
    { method: "POST", body },
  );
}

export interface MasterOwner {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  active: boolean;
  last_login_at: string;
  role_id: number;
  role_name: string;
  tenant_id: string;
}

export async function listMasterTenants(): Promise<MasterTenant[]> {
  const res = await api<{ tenants: MasterTenant[] }>("/api/master/tenants");
  return res.tenants;
}

export async function createMasterTenant(input: {
  business_name: string;
  agent_name?: string;
  disclosure_language?: string;
  tenant_id?: string;
  owner_username: string;
  owner_password: string;
  owner_email?: string;
  owner_display_name?: string;
}): Promise<{ tenant: MasterTenant; owner: MasterOwner }> {
  return api<{ tenant: MasterTenant; owner: MasterOwner }>(
    "/api/master/tenants",
    { method: "POST", body: input },
  );
}

export async function updateMasterTenant(
  tenantId: string,
  patch: { business_name?: string; agent_name?: string },
): Promise<MasterTenant> {
  const res = await api<{ tenant: MasterTenant }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}`,
    { method: "PATCH", body: patch },
  );
  return res.tenant;
}

export async function setMasterTenantActive(
  tenantId: string,
  active: boolean,
): Promise<MasterTenant> {
  const res = await api<{ tenant: MasterTenant }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/active`,
    { method: "POST", body: { active } },
  );
  return res.tenant;
}

/** §ai-on-off — flip a tenant's AI answering switch from Mission Control.
 * Writes the SAME agent_published flag the tenant's own dashboard switch
 * uses (one source of truth); NOT account suspension (see
 * setMasterTenantActive) and NOT the pilot lifecycle (masterTenantPilot). */
export async function setMasterTenantAgentPublished(
  tenantId: string,
  published: boolean,
): Promise<{ ok: boolean; published: boolean }> {
  return api<{ ok: boolean; published: boolean }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/agent-published`,
    { method: "POST", body: { published } },
  );
}

// §mission-control-ops — plan→price (AED/mo). Single source of truth on the
// client, mirroring master_store.PLAN_PRICES.
export const PLAN_PRICES: Record<string, number> = {
  Starter: 290,
  Growth: 690,
  Scale: 1490,
  Enterprise: 3200,
  // §pilot-plan — free founder-granted pilot; 0 mirrors the backend seed.
  Pilot: 0,
};
// PLAN_ORDER stays the PAID tiers only: it drives the "Change plan" buttons +
// order-form pickers, where Pilot is granted via its own lifecycle, not picked.
export const PLAN_ORDER = ["Starter", "Growth", "Scale", "Enterprise"];

// §billing.plan-catalog — master-editable plan pricing/details. PLAN_PRICES
// above stays as a static fallback; the catalog is the live source.
export interface PlanCatalogEntry {
  key: string; // stable id (Starter/Growth/Scale/Enterprise) — not editable
  name: string;
  price_aed: number; // per MONTH
  // §billing.annual — per-YEAR price + its own Stripe Price id. Defaults to
  // ~2 months free server-side when unset; master-editable here.
  price_aed_annual: number;
  description: string;
  stripe_price_id: string; // monthly Stripe Price id
  stripe_price_id_annual: string; // annual Stripe Price id
  active: boolean;
  order: number;
  /** §pilot-plan — true only for the free Pilot tier (server-pinned to 0 AED,
   * never billable); the wizard keys off this flag, not the key string. */
  pilot?: boolean;
}

/** Public-catalog shape: the internal Stripe ids are stripped server-side. */
export type PublicPlanEntry = Omit<
  PlanCatalogEntry,
  "stripe_price_id" | "stripe_price_id_annual"
>;

/** Full catalog incl. Stripe price ids (master-gated). */
export async function getPlanCatalog(): Promise<PlanCatalogEntry[]> {
  const res = await api<{ plans: PlanCatalogEntry[] }>("/api/master/plans");
  return res.plans;
}

export async function setPlanCatalog(
  plans: PlanCatalogEntry[],
): Promise<PlanCatalogEntry[]> {
  const res = await api<{ plans: PlanCatalogEntry[] }>("/api/master/plans", {
    method: "PUT",
    body: { plans },
  });
  return res.plans;
}

/** §pilot-plan — full public payload: the catalog PLUS the signup mode
 * (ONBOARDING_SIGNUP_MODE) so the wizard's pricing step can render pilot
 * ("review") mode without a second config endpoint. */
export async function getPublicCatalog(): Promise<{
  plans: PublicPlanEntry[];
  signup_mode: string;
}> {
  const res = await api<{ plans: PublicPlanEntry[]; signup_mode?: string }>(
    "/api/plans",
    { auth: false },
  );
  return { plans: res.plans, signup_mode: res.signup_mode || "open" };
}

/** Public catalog (active plans, no Stripe ids) for the onboarding pricing step. */
export async function getPublicPlans(): Promise<PublicPlanEntry[]> {
  const res = await getPublicCatalog();
  return res.plans;
}

export async function setMasterTenantPlan(
  tenantId: string,
  plan: string,
): Promise<{ ok: boolean; plan: string; mrr: number }> {
  return api<{ ok: boolean; plan: string; mrr: number }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/plan`,
    { method: "POST", body: { plan } },
  );
}

export async function listMasterOwners(tenantId: string): Promise<MasterOwner[]> {
  const res = await api<{ owners: MasterOwner[] }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/owners`,
  );
  return res.owners;
}

export async function createMasterOwner(
  tenantId: string,
  input: {
    username: string;
    password: string;
    display_name?: string;
    email?: string;
  },
): Promise<MasterOwner> {
  const res = await api<{ owner: MasterOwner }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/owners`,
    { method: "POST", body: input },
  );
  return res.owner;
}

export async function resetMasterOwnerPassword(
  tenantId: string,
  operatorId: number,
  password: string,
): Promise<{ ok: boolean; username: string }> {
  return api<{ ok: boolean; username: string }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/owners/${operatorId}/reset-password`,
    { method: "POST", body: { password } },
  );
}

export async function updateMasterOwner(
  tenantId: string,
  operatorId: number,
  patch: { email?: string | null; display_name?: string },
): Promise<MasterOwner> {
  const res = await api<{ owner: MasterOwner }>(
    `/api/master/tenants/${encodeURIComponent(tenantId)}/owners/${operatorId}`,
    { method: "PATCH", body: patch },
  );
  return res.owner;
}
